import React, { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import Icons from 'assets/icons';

interface TextareaErrorsNames {
  message?: string;
  [key: string]: unknown;
}

interface TextareaProps {
  textareaWrapperStyles?: string;
  textareaStyles?: string;
  customLabelStyles?: string;
  label?: string;
  placeholder?: string;
  maxLength?: number;
  onChange?: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  value?: string;
  labelFor?: string;
  rows?: number;
  errorsNames?: TextareaErrorsNames;
  helperText?: string;
  required?: boolean;
  showCharCount?: boolean;
  'data-testid'?: string;
  // RHF Controller `field` interop. Spread `{...field}` carries these.
  onBlur?: () => void;
  name?: string;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      textareaWrapperStyles = '',
      textareaStyles = '',
      customLabelStyles = '',
      label,
      placeholder,
      maxLength = 250,
      onChange,
      onKeyDown,
      disabled,
      value = '',
      labelFor = '',
      rows = 5,
      errorsNames = {},
      helperText,
      required,
      showCharCount = true,
      'data-testid': dataTestId = 'textarea-field',
      onBlur,
      name,
    },
    ref
  ) => {
    const handleChangeValue = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
      onChange?.(e.target.value);
    };

    const isValid = Object.keys(errorsNames).length === 0;

    const baseWrapperStyles = twMerge('w-full flex flex-col', textareaWrapperStyles);

    const baseStyles = twMerge(
      'w-full h-full border border-navy-200 focus-visible:outline-0 rounded-md py-2 px-3 focus:outline-none hover:border-navy-500 focus:border-navy-700 disabled:hover:border-navy-200 transition',
      disabled && 'bg-gray-100',
      !isValid && 'border-critical-600 focus:border-critical-600 hover:border-critical-600',
      isValid && 'border-navy-200',
      textareaStyles,
    );

    const labelClasses = twMerge(
      'font-semibold flex',
      !helperText && 'mb-2',
      customLabelStyles,
    );

    return (
      <div className={baseWrapperStyles}>
        {label && (
          <label htmlFor={labelFor} className={labelClasses}>
            {label}
            {required && !disabled ? (
              <Icons.RequiredIndicator
                customClass="w-[7px] h-[7px] ml-1 mt-1"
                data-testid={`${dataTestId}-required-indicator`}
              />
            ) : null}
          </label>
        )}

        {helperText && (
          <span className="text-sm mb-1 mt-1.5">{helperText}</span>
        )}
        <textarea
          ref={ref}
          className={baseStyles}
          placeholder={placeholder}
          value={value}
          onChange={handleChangeValue}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          name={name}
          maxLength={maxLength}
          disabled={disabled}
          id={labelFor}
          rows={rows}
          data-testid={dataTestId}
        />
        {!isValid && !disabled && (
          <span className="font-sm pt-1 text-critical-600 bg-white">
            {errorsNames.message}
          </span>
        )}
        {showCharCount && !disabled && (
          <span className="mt-1 text-sm">
            {value.length} of {maxLength} characters used
          </span>
        )}
      </div>
    );
  }
);

export default Textarea;
